import TurndownService from 'turndown';
import * as mammoth from 'mammoth';
import pdfParse from 'pdf-parse';
import csvParser from 'csv-parser';
import MarkdownIt from 'markdown-it';
import { JSDOM } from 'jsdom';
import type {
  Document,
  DocumentFormat,
  DocumentSource,
  DocumentMetadata,
  IngestionRequest,
  RAGEnv,
} from '../types';
import { DocumentProcessingError } from '../types';
import { generateId, sanitizeText, extractHeaders } from '../core/utils';

/**
 * Document loader that supports multiple formats and sources
 */
export class DocumentLoader {
  private turndownService: TurndownService;
  private markdownIt: MarkdownIt;
  private env: RAGEnv;

  constructor(env: RAGEnv) {
    this.env = env;
    
    // Initialize Turndown for HTML to Markdown conversion
    this.turndownService = new TurndownService({
      headingStyle: 'atx',
      hr: '---',
      bulletListMarker: '-',
      codeBlockStyle: 'fenced',
      fence: '```',
      emDelimiter: '*',
      strongDelimiter: '**',
    });
    
    // Initialize markdown parser
    this.markdownIt = new MarkdownIt({
      html: true,
      linkify: true,
      typographer: true,
    });
  }

  /**
   * Load a document from various sources
   */
  async loadDocument(request: IngestionRequest): Promise<Document> {
    try {
      const { source, type, format } = request;
      
      let content: string;
      let detectedFormat: DocumentFormat;
      let metadata: DocumentMetadata;

      switch (type) {
        case 'text':
          content = source;
          detectedFormat = format || 'txt';
          metadata = this.createMetadata({
            id: generateId(),
            format: detectedFormat,
            source: 'text',
            ...request.metadata,
          });
          break;

        case 'file':
          ({ content, format: detectedFormat, metadata } = await this.loadFromFile(source, request));
          break;

        case 'url':
          ({ content, format: detectedFormat, metadata } = await this.loadFromUrl(source, request));
          break;

        case 's3':
        case 'r2':
          ({ content, format: detectedFormat, metadata } = await this.loadFromStorage(source, type, request));
          break;

        case 'github':
          ({ content, format: detectedFormat, metadata } = await this.loadFromGitHub(source, request));
          break;

        default:
          throw new DocumentProcessingError('unknown', `Unsupported source type: ${type}`);
      }

      // Process content based on format
      const processedContent = await this.processContent(content, detectedFormat);
      
      return {
        id: metadata.id,
        content: processedContent,
        metadata,
      };
    } catch (error) {
      if (error instanceof DocumentProcessingError) {
        throw error;
      }
      throw new DocumentProcessingError('unknown', `Failed to load document: ${error}`, error as Error);
    }
  }

  /**
   * Load multiple documents in batch
   */
  async loadDocuments(requests: IngestionRequest[]): Promise<Document[]> {
    const results = await Promise.allSettled(
      requests.map(request => this.loadDocument(request))
    );

    const documents: Document[] = [];
    const errors: string[] = [];

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        documents.push(result.value);
      } else {
        errors.push(`Document ${index}: ${result.reason.message}`);
      }
    });

    if (errors.length > 0) {
      console.warn('Some documents failed to load:', errors);
    }

    return documents;
  }

  /**
   * Load document from file path
   */
  private async loadFromFile(filePath: string, request: IngestionRequest): Promise<{
    content: string;
    format: DocumentFormat;
    metadata: DocumentMetadata;
  }> {
    // In a real implementation, this would read from the file system
    // For Cloudflare Workers, files would typically be uploaded to R2 first
    throw new DocumentProcessingError('file', 'File system access not available in Cloudflare Workers. Use R2 storage instead.');
  }

  /**
   * Load document from URL
   */
  private async loadFromUrl(url: string, request: IngestionRequest): Promise<{
    content: string;
    format: DocumentFormat;
    metadata: DocumentMetadata;
  }> {
    try {
      const response = await fetch(url, {
        headers: {
          'User-Agent': 'Nexus RAG Pipeline/1.0',
          'Accept': 'text/html,application/xhtml+xml,application/xml,text/plain,application/pdf,*/*',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const contentType = response.headers.get('content-type') || '';
      const format = this.detectFormatFromContentType(contentType) || request.format || 'html';
      
      let content: string;
      if (format === 'pdf') {
        const buffer = await response.arrayBuffer();
        content = await this.extractFromPDF(buffer);
      } else {
        content = await response.text();
      }

      const metadata = this.createMetadata({
        id: generateId(),
        url,
        format,
        source: 'url',
        title: this.extractTitleFromUrl(url),
        fileSize: parseInt(response.headers.get('content-length') || '0'),
        ...request.metadata,
      });

      return { content, format, metadata };
    } catch (error) {
      throw new DocumentProcessingError('url', `Failed to fetch from URL: ${error}`, error as Error);
    }
  }

  /**
   * Load document from R2/S3 storage
   */
  private async loadFromStorage(key: string, storageType: 's3' | 'r2', request: IngestionRequest): Promise<{
    content: string;
    format: DocumentFormat;
    metadata: DocumentMetadata;
  }> {
    try {
      if (storageType === 'r2') {
        const object = await this.env.RAG_BUCKET.get(key);
        if (!object) {
          throw new Error(`Object not found: ${key}`);
        }

        const format = request.format || this.detectFormatFromFilename(key);
        let content: string;

        if (format === 'pdf') {
          const buffer = await object.arrayBuffer();
          content = await this.extractFromPDF(buffer);
        } else {
          content = await object.text();
        }

        const metadata = this.createMetadata({
          id: generateId(),
          filePath: key,
          format,
          source: 'r2',
          fileSize: object.size,
          title: this.extractTitleFromFilename(key),
          ...request.metadata,
        });

        return { content, format, metadata };
      } else {
        // S3 support would require AWS SDK
        throw new DocumentProcessingError('s3', 'S3 support not implemented yet');
      }
    } catch (error) {
      throw new DocumentProcessingError(storageType, `Failed to load from ${storageType}: ${error}`, error as Error);
    }
  }

  /**
   * Load document from GitHub
   */
  private async loadFromGitHub(repoPath: string, request: IngestionRequest): Promise<{
    content: string;
    format: DocumentFormat;
    metadata: DocumentMetadata;
  }> {
    try {
      // Parse GitHub URL: owner/repo/path or just path for raw files
      const [owner, repo, ...pathParts] = repoPath.split('/');
      const filePath = pathParts.join('/');
      
      // Use GitHub raw content URL
      const url = `https://raw.githubusercontent.com/${owner}/${repo}/main/${filePath}`;
      
      return await this.loadFromUrl(url, {
        ...request,
        type: 'url',
        metadata: {
          ...request.metadata,
          source: 'github',
          url,
        },
      });
    } catch (error) {
      throw new DocumentProcessingError('github', `Failed to load from GitHub: ${error}`, error as Error);
    }
  }

  /**
   * Process content based on format
   */
  private async processContent(content: string, format: DocumentFormat): Promise<string> {
    try {
      switch (format) {
        case 'html':
          return this.processHTML(content);
        case 'pdf':
          return this.processPDF(content); // Content is already extracted
        case 'docx':
          return await this.processDocx(content);
        case 'markdown':
          return this.processMarkdown(content);
        case 'csv':
          return await this.processCSV(content);
        case 'json':
          return this.processJSON(content);
        case 'xml':
          return this.processXML(content);
        case 'txt':
        default:
          return sanitizeText(content);
      }
    } catch (error) {
      throw new DocumentProcessingError(format, `Failed to process ${format} content: ${error}`, error as Error);
    }
  }

  /**
   * Process HTML content
   */
  private processHTML(html: string): string {
    // Parse HTML and extract meaningful content
    const dom = new JSDOM(html);
    const document = dom.window.document;

    // Remove script and style elements
    const scripts = document.querySelectorAll('script, style, nav, footer, aside');
    scripts.forEach(el => el.remove());

    // Try to find main content area
    let content = document.querySelector('main, article, .content, #content, .post, .entry');
    if (!content) {
      content = document.body;
    }

    // Convert to markdown for better structure preservation
    const markdown = this.turndownService.turndown(content.innerHTML);
    return sanitizeText(markdown);
  }

  /**
   * Process PDF content (already extracted)
   */
  private processPDF(content: string): string {
    return sanitizeText(content);
  }

  /**
   * Extract text from PDF buffer
   */
  private async extractFromPDF(buffer: ArrayBuffer): Promise<string> {
    try {
      const data = await pdfParse(Buffer.from(buffer));
      return data.text;
    } catch (error) {
      throw new Error(`PDF parsing failed: ${error}`);
    }
  }

  /**
   * Process DOCX content
   */
  private async processDocx(buffer: string): Promise<string> {
    try {
      // Note: In practice, this would need to handle binary data properly
      // This is a simplified implementation
      const bufferData = Buffer.from(buffer);
      const result = await mammoth.extractRawText({ arrayBuffer: bufferData.buffer as ArrayBuffer });
      return sanitizeText(result.value);
    } catch (error) {
      throw new Error(`DOCX parsing failed: ${error}`);
    }
  }

  /**
   * Process Markdown content
   */
  private processMarkdown(markdown: string): string {
    // Parse and re-render to normalize markdown
    const html = this.markdownIt.render(markdown);
    const dom = new JSDOM(html);
    const text = dom.window.document.body.textContent || '';
    return sanitizeText(text);
  }

  /**
   * Process CSV content
   */
  private async processCSV(csvContent: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const rows: any[] = [];
      const stream = require('stream').Readable.from([csvContent]);
      
      stream
        .pipe(csvParser())
        .on('data', (row: any) => rows.push(row))
        .on('end', () => {
          // Convert CSV to text format
          const text = rows.map(row => 
            Object.entries(row)
              .map(([key, value]) => `${key}: ${value}`)
              .join(', ')
          ).join('\n');
          resolve(sanitizeText(text));
        })
        .on('error', reject);
    });
  }

  /**
   * Process JSON content
   */
  private processJSON(jsonContent: string): string {
    try {
      const data = JSON.parse(jsonContent);
      // Convert JSON to readable text
      const text = this.jsonToText(data);
      return sanitizeText(text);
    } catch (error) {
      throw new Error(`JSON parsing failed: ${error}`);
    }
  }

  /**
   * Process XML content
   */
  private processXML(xmlContent: string): string {
    try {
      const dom = new JSDOM(xmlContent, { contentType: 'text/xml' });
      const text = dom.window.document.textContent || '';
      return sanitizeText(text);
    } catch (error) {
      throw new Error(`XML parsing failed: ${error}`);
    }
  }

  /**
   * Convert JSON to readable text
   */
  private jsonToText(obj: any, prefix: string = ''): string {
    if (typeof obj !== 'object' || obj === null) {
      return String(obj);
    }

    const lines: string[] = [];
    
    if (Array.isArray(obj)) {
      obj.forEach((item, index) => {
        const itemPrefix = `${prefix}[${index}]`;
        lines.push(`${itemPrefix}: ${this.jsonToText(item, itemPrefix)}`);
      });
    } else {
      Object.entries(obj).forEach(([key, value]) => {
        const itemPrefix = prefix ? `${prefix}.${key}` : key;
        if (typeof value === 'object' && value !== null) {
          lines.push(`${itemPrefix}:`);
          lines.push(this.jsonToText(value, itemPrefix));
        } else {
          lines.push(`${itemPrefix}: ${value}`);
        }
      });
    }

    return lines.join('\n');
  }

  /**
   * Create document metadata
   */
  private createMetadata(partial: Partial<DocumentMetadata>): DocumentMetadata {
    const timestamp = Date.now();
    return {
      id: partial.id || generateId(),
      format: partial.format!,
      source: partial.source!,
      createdAt: timestamp,
      updatedAt: timestamp,
      ...partial,
    };
  }

  /**
   * Detect format from content type
   */
  private detectFormatFromContentType(contentType: string): DocumentFormat | null {
    if (contentType.includes('text/html')) return 'html';
    if (contentType.includes('text/markdown')) return 'markdown';
    if (contentType.includes('text/plain')) return 'txt';
    if (contentType.includes('application/pdf')) return 'pdf';
    if (contentType.includes('application/vnd.openxmlformats-officedocument.wordprocessingml.document')) return 'docx';
    if (contentType.includes('text/csv')) return 'csv';
    if (contentType.includes('application/json')) return 'json';
    if (contentType.includes('application/xml') || contentType.includes('text/xml')) return 'xml';
    return null;
  }

  /**
   * Detect format from filename
   */
  private detectFormatFromFilename(filename: string): DocumentFormat {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'md': return 'markdown';
      case 'html': case 'htm': return 'html';
      case 'pdf': return 'pdf';
      case 'docx': return 'docx';
      case 'csv': return 'csv';
      case 'json': return 'json';
      case 'xml': return 'xml';
      case 'txt': default: return 'txt';
    }
  }

  /**
   * Extract title from URL
   */
  private extractTitleFromUrl(url: string): string {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const filename = pathname.split('/').pop() || '';
      return filename.split('.')[0] || urlObj.hostname;
    } catch {
      return 'Unknown Document';
    }
  }

  /**
   * Extract title from filename
   */
  private extractTitleFromFilename(filename: string): string {
    const name = filename.split('/').pop() || filename;
    return name.split('.')[0] || 'Unknown Document';
  }
}